import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Locale, UserListItem } from '../../../api/types';
import { Badge } from '../../../shared/ui/Badge';
import { Button } from '../../../shared/ui/Button';
import { Field } from '../../../shared/ui/Field';
import { Input } from '../../../shared/ui/Input';
import { Select } from '../../../shared/ui/Select';
import { Table } from '../../../shared/ui/Table';
import { useAppSettings } from '../../settings/hooks/useAppSettings';
import { useBlockUser } from '../hooks/useBlockUser';
import { useChangeUserAvailableLocales } from '../hooks/useChangeUserAvailableLocales';
import { useChangeUserPlan } from '../hooks/useChangeUserPlan';
import { useUnblockUser } from '../hooks/useUnblockUser';
import styles from './UsersTable.module.css';

interface UsersTableProps {
  users: UserListItem[];
}

const PLANS: Array<UserListItem['plan']> = ['FREE', 'PRO', 'ULTIMATE'];

function formatAvailableLocales(locales: Locale[] | null, t: (key: string) => string): string {
  if (locales === null) {
    return t('users.globalDefault');
  }
  if (locales.length === 0) {
    return t('users.globalDefault');
  }
  return locales.join(', ');
}

// PARA-106, AC-2/AC-3; PARA-137, AC-1/AC-2/AC-4; PARA-138: таблица
// пользователей с отображением плана, доступных языков, разблокировкой,
// сменой тарифа и изменением доступных локалей.
export function UsersTable({ users }: UsersTableProps): JSX.Element {
  const { t } = useTranslation();
  const { mutate: block, isPending: isBlocking } = useBlockUser();
  const { mutate: unblock, isPending: isUnblocking } = useUnblockUser();
  const { mutate: changePlan, isPending: isChangingPlan } = useChangeUserPlan();
  const { mutate: changeLanguages, isPending: isChangingLanguages } =
    useChangeUserAvailableLocales();
  const { data: settings } = useAppSettings();

  const [changingUser, setChangingUser] = useState<UserListItem | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<UserListItem['plan']>('FREE');
  const [planExpiresAt, setPlanExpiresAt] = useState<string>('');

  const [languageUser, setLanguageUser] = useState<UserListItem | null>(null);
  const [selectedLocales, setSelectedLocales] = useState<Set<Locale>>(new Set());

  const isPending = isBlocking || isUnblocking || isChangingPlan || isChangingLanguages;

  const openChangePlan = (user: UserListItem): void => {
    setChangingUser(user);
    setSelectedPlan(user.plan);
    setPlanExpiresAt('');
  };

  const closeChangePlan = (): void => {
    setChangingUser(null);
    setPlanExpiresAt('');
  };

  const handlePlanChange = (plan: UserListItem['plan']): void => {
    setSelectedPlan(plan);
    if (plan === 'FREE') {
      setPlanExpiresAt('');
    }
  };

  const submitChangePlan = (): void => {
    if (changingUser === null) {
      return;
    }
    const payload: { plan: UserListItem['plan']; plan_expires_at?: string } = {
      plan: selectedPlan,
    };
    if (selectedPlan !== 'FREE' && planExpiresAt !== '') {
      payload.plan_expires_at = `${planExpiresAt}T23:59:59Z`;
    }
    changePlan({ userId: changingUser.id, payload }, { onSuccess: closeChangePlan });
  };

  const supportedLocales = settings?.supported_locales ?? [];

  const openChangeLanguages = (user: UserListItem): void => {
    setLanguageUser(user);
    const initial =
      user.available_locales === null
        ? new Set<Locale>(supportedLocales)
        : new Set<Locale>(user.available_locales);
    setSelectedLocales(initial);
  };

  const closeChangeLanguages = (): void => {
    setLanguageUser(null);
    setSelectedLocales(new Set());
  };

  const toggleLocale = (locale: Locale): void => {
    setSelectedLocales((prev) => {
      const next = new Set(prev);
      if (next.has(locale)) {
        next.delete(locale);
      } else {
        next.add(locale);
      }
      return next;
    });
  };

  const useGlobalDefault = (): void => {
    setSelectedLocales(new Set());
  };

  const submitChangeLanguages = (): void => {
    if (languageUser === null) {
      return;
    }
    const payload = { available_locales: Array.from(selectedLocales) };
    changeLanguages({ userId: languageUser.id, payload }, { onSuccess: closeChangeLanguages });
  };

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>{t('users.id')}</th>
            <th>{t('users.telegramUserId')}</th>
            <th>{t('users.username')}</th>
            <th>{t('users.tasksCount')}</th>
            <th>{t('users.plan')}</th>
            <th>{t('users.availableLocales')}</th>
            <th>{t('users.status')}</th>
            <th>{t('users.createdAt')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td className={styles.num}>{user.id}</td>
              <td className={styles.num}>{user.telegram_user_id}</td>
              <td>{user.username ?? '—'}</td>
              <td>{user.tasks_count}</td>
              <td>{t(`users.planValue.${user.plan}`)}</td>
              <td>{formatAvailableLocales(user.available_locales, t)}</td>
              <td>
                {user.is_banned ? (
                  <Badge tone="muted">{t('users.blocked')}</Badge>
                ) : (
                  <Badge tone="positive">{t('users.active')}</Badge>
                )}
              </td>
              <td className={styles.num}>{user.created_at ?? '—'}</td>
              <td className={styles.actions}>
                {user.is_banned ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isPending}
                    onClick={() => unblock(user.id)}
                  >
                    {t('users.unblock')}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isPending}
                    onClick={() => block(user.id)}
                  >
                    {t('users.block')}
                  </Button>
                )}
                <Button
                  variant="soft"
                  size="sm"
                  disabled={isPending}
                  onClick={() => openChangePlan(user)}
                  className={styles.changePlanButton}
                >
                  {t('users.changePlan')}
                </Button>
                <Button
                  variant="soft"
                  size="sm"
                  disabled={isPending || supportedLocales.length === 0}
                  onClick={() => openChangeLanguages(user)}
                >
                  {t('users.changeLanguages')}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {changingUser !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-plan-title"
          className={styles.dialogOverlay}
        >
          <div className={styles.dialogContent}>
            <h3 id="change-plan-title">
              {t('users.changePlanTitle', {
                username: changingUser.username ?? changingUser.id,
              })}
            </h3>
            <Field label={t('users.plan')} htmlFor="plan-select" className={styles.dialogField}>
              <Select
                id="plan-select"
                value={selectedPlan}
                onChange={(e) => handlePlanChange(e.target.value as UserListItem['plan'])}
              >
                {PLANS.map((plan) => (
                  <option key={plan} value={plan}>
                    {t(`users.planValue.${plan}`)}
                  </option>
                ))}
              </Select>
            </Field>
            {selectedPlan !== 'FREE' && (
              <Field
                label={t('users.planExpiresAt')}
                htmlFor="plan-expires-at"
                className={styles.dialogField}
              >
                <Input
                  id="plan-expires-at"
                  type="date"
                  value={planExpiresAt}
                  onChange={(e) => setPlanExpiresAt(e.target.value)}
                />
              </Field>
            )}
            <div className={styles.dialogActions}>
              <Button
                variant="ghost"
                onClick={closeChangePlan}
                disabled={isChangingPlan}
                className={styles.dialogActionButton}
              >
                {t('users.changePlanCancel')}
              </Button>
              <Button variant="primary" onClick={submitChangePlan} disabled={isChangingPlan}>
                {t('users.changePlanSubmit')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {languageUser !== null && supportedLocales.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-languages-title"
          className={styles.dialogOverlay}
        >
          <div className={styles.dialogContent}>
            <h3 id="change-languages-title">
              {t('users.changeLanguagesTitle', {
                username: languageUser.username ?? languageUser.id,
              })}
            </h3>
            <div className={styles.dialogField}>
              {supportedLocales.map((locale) => (
                <label key={locale} className={styles.localeOption}>
                  <input
                    type="checkbox"
                    checked={selectedLocales.has(locale)}
                    onChange={() => toggleLocale(locale)}
                  />
                  {locale.toUpperCase()}
                </label>
              ))}
            </div>
            <div className={styles.dialogField}>
              <Button
                variant="ghost"
                size="sm"
                onClick={useGlobalDefault}
                disabled={isChangingLanguages}
              >
                {t('users.useGlobalDefault')}
              </Button>
            </div>
            <div className={styles.dialogActions}>
              <Button
                variant="ghost"
                onClick={closeChangeLanguages}
                disabled={isChangingLanguages}
                className={styles.dialogActionButton}
              >
                {t('users.changeLanguagesCancel')}
              </Button>
              <Button
                variant="primary"
                onClick={submitChangeLanguages}
                disabled={isChangingLanguages}
              >
                {t('users.changeLanguagesSubmit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
